import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./oversiktPreview.css";
import logoOrange from "./assets/Oransje_Logo.svg";
import symbolOnDark from "./assets/symbol-on-dark.svg";
import { fetchCities, fetchCityById, fetchCityBySlug } from "./cities";
import {
  CATEGORY_TYPE,
  fetchLocationsNear,
  fetchEventsNear,
  fetchLocationDetail,
  fetchLocationBySlug,
  withDistance,
  priceTier,
  openStatus,
  formatHoursWeek,
  fetchReviewsFor,
  pickLang,
  MENU_VOCAB,
  TYPE_VOCAB,
  servesAllTerms,
  menuTermCount,
  matchedMenuTerms,
  menuItemWords,
} from "./locations";
import { buildOversiktPath, buildPlacePath, categoryFromSlug } from "./seo";
import { AdSlot, interleaveAds } from "./adSlot";
import { AdConsentBanner, useAdConsent } from "./adConsent";
import { useIsMobile, isMobileNow } from "./useIsMobile";
import Supercluster from "supercluster";
import restaurantIcon from "./assets/mapIcons/restaurant.png";
import barIcon from "./assets/mapIcons/bar.png";
import cafeIcon from "./assets/mapIcons/cafe.png";
import activityIcon from "./assets/mapIcons/activity.png";
import eventIcon from "./assets/mapIcons/event.png";

/*
 * Hidden preview of the redesigned Mapli search-results / place-profile
 * page. Reached from /new-landing-preview's "Se alt ->" link, or directly
 * via /new-oversikt-preview?city=<cities docId>&category=restaurants
 * (or ?lat=&lng=&name=&radius= for a city that only exists via the
 * landing page's Places fallback, with no `cities` doc yet).
 */

const SAVED_KEY = "mapli_oversikt_saved_v1";
const MAPBOX_TOKEN = "pk.eyJ1Ijoic3RlZmZlbmxpZTEiLCJhIjoiY2xqNDBzNnpyMDlxeTNlczFidW9seGxsOSJ9.DNY1zitSfG0YZ3SvjARdHg";
// Same custom Mapbox style the app's map.js uses (components/map.js in the
// Mapli mobile app repo), not the generic mapbox/streets style.
const MAP_STYLE_URL = "mapbox://styles/steffenlie1/clo31gw1500jd01r2c3y3g1hm";

let mapGlModule = null;
function useMapGl() {
  const [mod, setMod] = useState(mapGlModule);
  useEffect(() => {
    if (mapGlModule) return;
    // mapbox-gl.css is required for the map canvas/controls to size and
    // position correctly — react-map-gl doesn't inject it for you, and
    // without it the map renders broken (this was the actual bug).
    Promise.all([import("react-map-gl"), import("mapbox-gl/dist/mapbox-gl.css")]).then(([m]) => {
      mapGlModule = m;
      setMod(m);
    });
  }, []);
  return mod;
}

// Same category -> marker-icon rule as the app's map.js SymbolLayer
// expression: a specific secondaryType icon (bar/cafe) wins when present,
// otherwise fall back to the broad category icon.
const MAP_ICONS = { restaurant: restaurantIcon, bar: barIcon, cafe: cafeIcon, activity: activityIcon, event: eventIcon };
function iconForLocation(loc) {
  const secondary = (loc.secondaryType || "").toLowerCase();
  if (MAP_ICONS[secondary]) return MAP_ICONS[secondary];
  if (loc.type === "activity") return MAP_ICONS.activity;
  if (loc.type === "event") return MAP_ICONS.event;
  return MAP_ICONS.restaurant;
}

const REVIEW_SOURCE_ORDER = ["tripadvisor", "google", "yelp"];
const REVIEW_SOURCE_LABEL = { tripadvisor: "Tripadvisor", google: "Google", yelp: "Yelp" };

const COPY = {
  no: {
    backToHome: "Mapli",
    searchPlaceholder: "Bytt by",
    savedNav: "Lagret",
    catRest: "Restauranter",
    catAct: "Aktiviteter",
    catEv: "Arrangementer",
    resultCount: (n) => `${n} treff`,
    noResults: "Ingen steder matcher filtrene dine akkurat nå.",
    facetMenu: "På menyen",
    menuSearchPlaceholder: "Søk i menyordene, f.eks. burger",
    noMenuMatches: "Ingen menyord treffer søket.",
    vocabCountSuffix: (city) => `menyord i ${city}`,
    andHint: "Viser bare steder som har alle de valgte ordene.",
    facetType: "Type",
    facetPrice: "Prisnivå",
    facetRadius: "Avstand",
    clearFilters: "Nullstill filtre",
    filterToggle: "Filtre",
    sortLabel: "Sorter",
    sortNearest: "Nærmest",
    sortRating: "Høyest vurdert",
    sortDate: "Dato",
    sortAz: "A-Å",
    mapShow: "Vis kart",
    mapHide: "Skjul kart",
    mapHint: "Klikk på et punkt for å åpne stedet",
    savedTitle: "Lagrede steder",
    savedEmpty: "Du har ikke lagret noen steder ennå.",
    noReviews: "Ingen omtaler",
    onTheMenu: "På menyen:",
    moreLabel: " Les mer",
    backLabel: "Tilbake",
    gallery: "Bilder",
    viewLarger: "Se stort bilde",
    hours: "Åpningstider",
    reserve: "Kontakt",
    phone: "Telefon",
    website: "Nettside",
    findUs: "Finn oss",
    tabAbout: "Om",
    tabMenu: "Meny",
    tabActivities: "Aktiviteter",
    tabReviews: "Omtaler",
    tabOrganiser: "Til arrangøren",
    menuHeading: "Meny",
    noMenu: "Ingen meny registrert ennå.",
    noActivities: "Ingen aktiviteter registrert ennå.",
    eventInfo: "Praktisk informasjon",
    allergyInfo: "Allergiinformasjon",
    lastUpdated: (d) => `Sist oppdatert: ${d}`,
    overallRating: "Samlet vurdering",
    overallNote: "Gjennomsnittsvurdering regnet ut fra vurderingene fra Tripadvisor, Google og Yelp",
    reviewsHeadingFor: (source) => `Siste 5 ${source} omtaler`,
    reviewCountSuffix: "omtaler",
    visitedLabel: (d) => `Besøkte: ${d}`,
    openNow: "Åpent nå",
    closesAt: (t) => `Stenger ${t}`,
    opensAt: (t) => `Åpner ${t}`,
    closedToday: "Stengt i dag",
    appTitle: "Ta Mapli med ut",
    appBody: "Kartet, filtrene og listen — i lomma.",
    footer: "© 2026 Mapli",
    days: ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"],
    loading: "Laster …",
  },
  en: {
    backToHome: "Mapli",
    searchPlaceholder: "Change city",
    savedNav: "Saved",
    catRest: "Restaurants",
    catAct: "Activities",
    catEv: "Events",
    resultCount: (n) => `${n} results`,
    noResults: "Nothing matches your filters right now.",
    facetMenu: "On the menu",
    menuSearchPlaceholder: "Search menu words, e.g. burger",
    noMenuMatches: "No menu words match that search.",
    vocabCountSuffix: (city) => `menu words in ${city}`,
    andHint: "Only showing places that have every selected word.",
    facetType: "Type",
    facetPrice: "Price",
    facetRadius: "Distance",
    clearFilters: "Clear filters",
    filterToggle: "Filters",
    sortLabel: "Sort",
    sortNearest: "Nearest",
    sortRating: "Highest rated",
    sortDate: "Date",
    sortAz: "A-Z",
    mapShow: "Show map",
    mapHide: "Hide map",
    mapHint: "Click a pin to open the place",
    savedTitle: "Saved places",
    savedEmpty: "You haven't saved anything yet.",
    noReviews: "No reviews",
    onTheMenu: "On the menu:",
    moreLabel: " Read more",
    backLabel: "Back",
    gallery: "Photos",
    viewLarger: "View larger image",
    hours: "Opening hours",
    reserve: "Contact",
    phone: "Phone",
    website: "Website",
    findUs: "Find us",
    tabAbout: "About",
    tabMenu: "Menu",
    tabActivities: "Activities",
    tabReviews: "Reviews",
    tabOrganiser: "To the organiser",
    menuHeading: "Menu",
    noMenu: "No menu on file yet.",
    noActivities: "No activities on file yet.",
    eventInfo: "Practical information",
    allergyInfo: "Allergy information",
    lastUpdated: (d) => `Last updated: ${d}`,
    overallRating: "Overall rating",
    overallNote: "Average rating calculated from the ratings on Tripadvisor, Google and Yelp",
    reviewsHeadingFor: (source) => `Latest 5 ${source} reviews`,
    reviewCountSuffix: "reviews",
    visitedLabel: (d) => `Visited: ${d}`,
    openNow: "Open now",
    closesAt: (t) => `Closes ${t}`,
    opensAt: (t) => `Opens ${t}`,
    closedToday: "Closed today",
    appTitle: "Take Mapli with you",
    appBody: "The map, the filters and the list — in your pocket.",
    footer: "© 2026 Mapli",
    days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    loading: "Loading …",
  },
};

const CATEGORY_KEYS = ["restaurants", "activities", "events"];
const PRICE_TIERS = [
  ["low", 1],
  ["mid", 2],
  ["high", 3],
];

function Icon({ name, size = 22, color = "currentColor", style }) {
  return (
    <span className="mapli-icon" aria-hidden="true" style={{ fontSize: size, color, ...style }}>
      {name}
    </span>
  );
}

function LogoWell({ src, alt, size = 64 }) {
  return (
    <div
      style={{
        flex: "0 0 auto",
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        background: "var(--surface-app)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
      {src ? (
        <img src={src} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ font: "700 26px/1 var(--font-ui)", color: "var(--ink-300)" }}>{(alt || "?").slice(0, 1)}</span>
      )}
    </div>
  );
}

// Full-screen photo viewer for the profile gallery. `onClose`/`onPrev`/
// `onNext` are only wired up by the caller when there's more than one photo.
function Lightbox({ src, onClose, onPrev, onNext, label }) {
  return (
    <div
      role="dialog"
      aria-label={label}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,.85)",
        display: "grid",
        placeItems: "center",
        cursor: "zoom-out",
      }}>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          border: "none",
          background: "rgba(255,255,255,.15)",
          color: "#fff",
          borderRadius: "50%",
          width: 40,
          height: 40,
          fontSize: 20,
          cursor: "pointer",
        }}>
        ✕
      </button>
      {onPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label="Previous"
          style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", border: "none", background: "rgba(255,255,255,.15)", color: "#fff", borderRadius: "50%", width: 44, height: 44, fontSize: 20, cursor: "pointer" }}>
          ‹
        </button>
      )}
      {onNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Next"
          style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", border: "none", background: "rgba(255,255,255,.15)", color: "#fff", borderRadius: "50%", width: 44, height: 44, fontSize: 20, cursor: "pointer" }}>
          ›
        </button>
      )}
      <img
        src={src}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", cursor: "default", boxShadow: "0 8px 40px rgba(0,0,0,.5)" }}
      />
    </div>
  );
}

// Ported from the design file's own RatingStars component: 5 stars, each
// independently clipped to its own fractional fill (star i is filled by
// clamp(rating - i, 0, 1)) — a continuous per-star fill, not one rounded
// row-level percentage.
function RatingStars({ rating, size = 18 }) {
  return (
    <span style={{ display: "inline-flex", gap: 1 }}>
      {Array.from({ length: 5 }).map((_, i) => {
        const fill = Math.max(0, Math.min(1, rating - i)) * 100;
        const filledVariation = { fontVariationSettings: `"FILL" 1, "wght" 500, "GRAD" 0, "opsz" ${size}` };
        return (
          <span key={i} style={{ position: "relative", width: size, height: size, display: "inline-block" }}>
            <Icon name="star" size={size} color="var(--star-empty)" style={{ position: "absolute", inset: 0, ...filledVariation }} />
            <span style={{ position: "absolute", inset: 0, overflow: "hidden", width: `${fill}%` }}>
              <Icon name="star" size={size} color="var(--category)" style={filledVariation} />
            </span>
          </span>
        );
      })}
    </span>
  );
}

// The card only shows the star row + "Ingen omtaler" fallback, same as
// locationCard.js — the per-source (Google/Tripadvisor/Yelp) breakdown only
// appears on the profile's Reviews tab, not here.
function RatingSummary({ location, t }) {
  if (!location.overallRating) {
    return <span style={{ font: "var(--type-body)", fontStyle: "italic", color: "var(--ink-500)" }}>{t.noReviews}</span>;
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <RatingStars rating={location.overallRating} />
      <span style={{ font: "var(--type-body-strong)", color: "var(--ink-900)" }}>{location.overallRating.toFixed(1)}</span>
    </div>
  );
}

// Remounted with key={location.id} in the menu tab so switching profiles
// starts with a fresh open/closed state instead of carrying over indices.
function MenuAccordion({ cats, currency }) {
  const [open, setOpen] = useState(() => new Set([0]));
  const toggle = (i) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };
  return cats.map((cat, i) => {
    const isOpen = open.has(i);
    return (
      <section key={i} style={{ borderBottom: "1px solid var(--border-hairline)", paddingBottom: 12 }}>
        <button
          type="button"
          onClick={() => toggle(i)}
          aria-expanded={isOpen}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: "none",
            border: "none",
            padding: 0,
            marginBottom: isOpen ? 10 : 0,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <span style={{ font: "var(--type-body-strong)", fontSize: 19, color: "var(--ink-900)" }}>{cat.category}</span>
          <span
            style={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: "50%",
              background: "var(--category)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon
              name="expand_more"
              size={20}
              color="#fff"
              style={{ transition: "transform 0.15s ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </span>
        </button>
        {isOpen && (
          <div style={{ display: "grid", gap: 12 }}>
            {(cat.menuItems || []).map((name, idx) => (
              <div key={idx} style={{ display: "grid", gap: 2 }}>
                <span style={{ font: "var(--type-body-strong)", color: "var(--ink-900)" }}>{name}</span>
                {cat.prices?.[idx] != null && <span style={{ font: "var(--type-body-strong)", color: "var(--category)" }}>{cat.prices[idx]} {currency}</span>}
                {cat.descriptions?.[idx] && <span style={{ font: "var(--type-body)", color: "var(--ink-900)" }}>{cat.descriptions[idx]}</span>}
                {cat.alergener?.[idx] && <span style={{ font: "var(--type-body)", color: "var(--ink-700)" }}>{cat.alergener[idx]}</span>}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  });
}

// tagsLang/locationTagsLang keys are "nb_NO"/"en_US", not "no"/"en".
function langKeyFor(lang) {
  return lang === "no" ? "nb_NO" : "en_US";
}

// Displayed tags come from locationTagsLang (a separate "main tags" set
// from the tagsLang used for filtering) — see locationCard.js. pickLang
// applies the selected-language-else-English rule; locationTags (the
// plain, non-language array) is only the last-resort fallback.
function displayTags(location, langKey) {
  const tags = pickLang(location.locationTagsLang, langKey);
  return tags?.length ? tags : location.locationTags || [];
}

function displayAbout(location, langKey) {
  return pickLang(location.aboutLang, langKey) || location.about || "";
}

// Maps a raw `lokasjoner` type back to the URL category it belongs under —
// needed when linking to an event's organiser, which is a real restaurant/
// activity, not necessarily whatever category tab is currently active.
function categoryForType(type) {
  if (type === "restaurant") return "restaurants";
  if (type === "activity") return "activities";
  return null;
}

// Turns bare URLs inside plain text into clickable links — same regex the
// app's renderTextWithLinks uses, since aboutEvent/aboutEventLang embed the
// ticket link as plain text, not a separate field.
function linkifyText(text) {
  return text.split(/(https?:\/\/\S+|www\.\S+)/g).map((part, i) =>
    /^(https?:\/\/|www\.)/.test(part) ? (
      <a key={i} href={part.startsWith("http") ? part : `https://${part}`} target="_blank" rel="noreferrer" style={{ color: "var(--text-link)" }}>
        {part}
      </a>
    ) : (
      part
    ),
  );
}

// Compact "5. des · 14:00" / "Dec 5 · 14:00" for the result-row meta line —
// eventDate is unix seconds, eventTime is already a preformatted "HH:MM".
function formatEventWhen(loc, lang) {
  if (!loc.eventDate) return null;
  const date = new Date(loc.eventDate * 1000).toLocaleDateString(lang === "no" ? "nb-NO" : "en-US", {
    day: "numeric",
    month: "short",
  });
  return loc.eventTime ? `${date} · ${loc.eventTime}` : date;
}

// Server-rendered initial data (see functions/seoRender.js), read exactly
// once per page load and removed immediately — later client-side
// navigation to a different city/category must always do a real fetch, not
// accidentally reuse this. Confirmed via Search Console's URL Inspection
// tool that without this, Google's renderer was snapshotting the page
// mid-"Laster..." (the client fetch hadn't resolved yet), which reads as
// "no content" to Search indexing, AdSense's ad-quality review, and link
// previews alike.
function readPreload() {
  if (typeof window === "undefined" || !window.__MAPLI_PRELOAD__) return null;
  const data = window.__MAPLI_PRELOAD__;
  delete window.__MAPLI_PRELOAD__;
  return data;
}

const OversiktPreview = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const routeParams = useParams(); // { country, city, category } on the pretty /:lang/... route
  const location = useLocation();
  const routeLang = location.pathname.startsWith("/en/") ? "en" : location.pathname.startsWith("/no/") ? "no" : null;
  const isPrettyRoute = Boolean(routeParams.city && routeParams.country);

  const [lang, setLang] = useState(routeLang || "no");
  const { consent: adConsent, choose: chooseAdConsent } = useAdConsent();
  const isMobile = useIsMobile();

  // Read once, stable for this component instance's whole lifetime — a
  // later client-side navigation to a different city/category must never
  // see this again (readPreload() already deletes the global on first read,
  // this just avoids re-reading undefined on every re-render).
  const [preload] = useState(readPreload);

  const [city, setCity] = useState(() => preload?.city || null);
  const [cityLoading, setCityLoading] = useState(() => !preload?.city);

  const [allLocations, setAllLocations] = useState(() => (preload && preload.category !== "events" ? preload.locations : []));
  const [allEvents, setAllEvents] = useState(() => (preload && preload.category === "events" ? preload.locations : []));
  const [locationsLoading, setLocationsLoading] = useState(() => !preload);

  const [category, setCategory] = useState(searchParams.get("category") || "restaurants");
  const [view, setView] = useState("list"); // "list" | "saved" | "profile"
  const [profileOrigin, setProfileOrigin] = useState("list");
  const [selectedId, setSelectedId] = useState(null);
  const [profileTab, setProfileTab] = useState("about");
  const [lightboxIndex, setLightboxIndex] = useState(null); // index into selectedLocation.gallery, or null when closed

  // Defaults closed on mobile — with filters/list/map stacked into one
  // column there, an open map means scrolling past it to reach results.
  const [mapOpen, setMapOpen] = useState(() => !isMobileNow());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [mapViewport, setMapViewport] = useState(null); // { zoom, bounds: [w,s,e,n] } — set once the map first loads
  const [mapError, setMapError] = useState(null);
  const [sortKey, setSortKey] = useState("distance");
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);

  const [radiusKm, setRadiusKm] = useState(25);
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [selectedPrices, setSelectedPrices] = useState(new Set());
  const [menuQuery, setMenuQuery] = useState("");
  const [menuTerms, setMenuTerms] = useState([]);

  const [savedIds, setSavedIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(SAVED_KEY) || "[]"));
    } catch {
      return new Set();
    }
  });

  const [allCities, setAllCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [citySearchOpen, setCitySearchOpen] = useState(false);
  const citySearchRef = useRef(null);

  const [reviews, setReviews] = useState({}); // locationId -> array | undefined (loading)

  const t = COPY[lang];

  useEffect(() => {
    if (routeLang) setLang(routeLang);
  }, [routeLang]);

  // Load the picked city: the pretty /:lang/:country/:city/:category route
  // resolves by slug; the legacy /new-oversikt-preview route prefers a real
  // `cities` doc by id, falling back to raw lat/lng/name/radius query
  // params for a Places-fallback pick that has no `cities` doc yet (see
  // NewLandingPreview's Tier 2 flow — that case can't build a pretty URL
  // since there's no countryCode to slug).
  // Deliberately excludes routeParams.category: switching category tabs
  // must never re-trigger this (it'd refetch the city and every location
  // in it just to change which tab is active — allLocations already has
  // every category, categoryLocations just filters it client-side).
  const skipInitialCityFetch = useRef(Boolean(preload?.city));
  useEffect(() => {
    if (skipInitialCityFetch.current) {
      skipInitialCityFetch.current = false;
      return;
    }
    const cityId = searchParams.get("city");
    const run = async () => {
      if (isPrettyRoute) {
        const resolvedLang = routeLang || "no";
        const found = await fetchCityBySlug(routeParams.country, routeParams.city, resolvedLang);
        setCity(found);
      } else if (cityId) {
        const found = await fetchCityById(cityId);
        setCity(found);
      } else {
        const lat = parseFloat(searchParams.get("lat"));
        const lng = parseFloat(searchParams.get("lng"));
        const name = searchParams.get("name") || "";
        const radius = parseFloat(searchParams.get("radius")) || 25;
        setCity(Number.isFinite(lat) && Number.isFinite(lng) ? { id: null, name, lat, lng, radiusKm: radius } : null);
      }
      setCityLoading(false);
    };
    setCityLoading(true);
    run();
  }, [isPrettyRoute, routeParams.country, routeParams.city, routeLang, searchParams]);

  // Cheap, synchronous sync of the active category tab from the URL — no
  // fetch involved, so this is instant on every tab click / back-button
  // navigation, unlike the city-resolution effect above.
  useEffect(() => {
    if (!isPrettyRoute) return;
    setCategory(categoryFromSlug(routeParams.category, routeLang || "no") || "restaurants");
  }, [isPrettyRoute, routeParams.category, routeLang]);

  const skipInitialLocationsFetch = useRef(Boolean(preload));
  useEffect(() => {
    if (!city) return;
    setRadiusKm(city.radiusKm || 25);

    if (skipInitialLocationsFetch.current) {
      skipInitialLocationsFetch.current = false;
      // Preload only ever covers the ONE category the page was requested
      // for (see fetchPreloadLocations in functions/seoRender.js) — for
      // "restaurants"/"activities" that's a `lokasjoner` set pre-filtered
      // to just that one type, never the full restaurants+activities set
      // `allLocations` needs to serve every tab. So locations always needs
      // a real fetch unless preload.category was "events" (in which case
      // allLocations' initial state is already [] — see its useState
      // above), and events only needs one when preload didn't already
      // cover them. Without this, whichever category tab the page wasn't
      // opened on stayed permanently empty, since this effect only reruns
      // on city change, not on category switches.
      const needEvents = preload.category !== "events";
      fetchLocationsNear(city).then(setAllLocations).catch(() => {});
      if (needEvents) fetchEventsNear(city).then(setAllEvents).catch(() => {});
      return;
    }

    setLocationsLoading(true);
    // Events live in their own collection (see fetchEventsNear), so they're
    // fetched separately from restaurants/activities, in parallel — same
    // shape as the app's own Promise.all([resQuery, eventQuery, ...]).
    Promise.all([
      fetchLocationsNear(city).catch(() => []),
      fetchEventsNear(city).catch(() => []),
    ])
      .then(([locations, events]) => {
        setAllLocations(locations);
        setAllEvents(events);
      })
      .finally(() => setLocationsLoading(false));
  }, [city]);

  useEffect(() => {
    fetchCities().then(setAllCities).catch(() => {});
  }, []);

  const langKey = langKeyFor(lang);

  // Reset category-scoped facets when switching tabs (menu words only make
  // sense for restaurants; type/price options are category-specific too).
  // Sort default is category-specific too: events start sorted by date
  // (there's no rating to sort by), everything else starts by distance.
  useEffect(() => {
    setMenuTerms([]);
    setMenuQuery("");
    setSelectedTypes(new Set());
    setSelectedPrices(new Set());
    setSortKey(category === "events" ? "date" : "distance");
  }, [category]);

  useEffect(() => {
    const onOutsideClick = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
      if (citySearchRef.current && !citySearchRef.current.contains(e.target)) setCitySearchOpen(false);
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, []);

  const toggleSaved = (id) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(SAVED_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const categoryLocations = useMemo(
    () => (category === "events" ? allEvents : allLocations.filter((l) => l.type === CATEGORY_TYPE[category])),
    [allLocations, allEvents, category],
  );
  const withDist = useMemo(() => (city ? withDistance(categoryLocations, city) : categoryLocations), [categoryLocations, city]);
  const radiusFiltered = useMemo(
    () => withDist.filter((l) => l.distanceKm === null || l.distanceKm <= radiusKm),
    [withDist, radiusKm],
  );

  // Ported from the design's `passes(p, cat, ignore)`: does a location
  // clear every facet *other* than `ignore`? Used both for the main result
  // list (ignore = null) and for each facet's own option counts (ignore =
  // that facet), so a count reflects "if I add this option, holding every
  // other filter fixed" rather than the whole unfiltered set.
  const passesOtherFacets = useCallback(
    (loc, ignore) => {
      if (ignore !== "types" && selectedTypes.size > 0) {
        const tags = displayTags(loc, langKey).map((tag) => tag.toLowerCase());
        if (![...selectedTypes].some((tg) => tags.includes(tg.toLowerCase()))) return false;
      }
      if (ignore !== "prices" && selectedPrices.size > 0 && !selectedPrices.has(priceTier(loc))) return false;
      if (ignore !== "menu" && category === "restaurants" && menuTerms.length > 0 && !servesAllTerms(loc, menuTerms, langKey)) return false;
      return true;
    },
    [selectedTypes, selectedPrices, menuTerms, langKey, category],
  );

  // "Type" facet: locationTagsLang alone still isn't clean — it's free-form
  // per-business input, so raw frequency has a long tail of one-off rare
  // cuisines and stray brand names mixed in with the common ones. Curated
  // TYPE_VOCAB (same fix pattern as MENU_VOCAB) intersected with whatever's
  // actually present in the current results is what actually shows here.
  const typeFacetOptions = useMemo(() => {
    const seen = [];
    radiusFiltered.forEach((l) => {
      displayTags(l, langKey).forEach((tag) => {
        if (!seen.includes(tag) && TYPE_VOCAB.some((v) => v.toLowerCase() === tag.toLowerCase())) seen.push(tag);
      });
    });
    return seen.map((tag) => [
      tag,
      radiusFiltered.filter((l) => passesOtherFacets(l, "types") && displayTags(l, langKey).some((tg) => tg.toLowerCase() === tag.toLowerCase())).length,
    ]);
  }, [radiusFiltered, langKey, passesOtherFacets]);

  const priceFacetOptions = useMemo(() => {
    return PRICE_TIERS.map(([key, tierNum]) => [
      key,
      tierNum,
      radiusFiltered.filter((l) => passesOtherFacets(l, "prices") && priceTier(l) === key).length,
    ]);
  }, [radiusFiltered, passesOtherFacets]);

  // Ported verbatim from the design: while typing, autocomplete against
  // the whole curated vocabulary; with an empty query, only show words
  // that actually appear somewhere in the current candidates' menus.
  const menuSuggestions = useMemo(() => {
    if (category !== "restaurants") return [];
    const mq = menuQuery.trim().toLowerCase();

    if (!mq) {
      // Nothing typed yet: just browse the curated common-word list.
      return MENU_VOCAB.filter((w) => menuTermCount(radiusFiltered, w, langKey) > 0)
        .filter((w) => !menuTerms.includes(w))
        .map((w) => ({ w, n: menuTermCount(radiusFiltered, w, langKey) }))
        .sort((a, b) => b.n - a.n || a.w.localeCompare(b.w))
        .slice(0, 12);
    }

    // Typing: search the curated list AND every real word pulled live from
    // this city's actual menus, so specific dishes/ingredients that never
    // made the curated ~180-word list are still findable. Counted with the
    // same menuTermCount used everywhere else, so behavior/scoring stays
    // identical regardless of which pool a word came from.
    const counts = new Map();
    MENU_VOCAB.forEach((w) => {
      if (w.includes(mq)) counts.set(w, menuTermCount(radiusFiltered, w, langKey));
    });
    radiusFiltered.forEach((loc) => {
      menuItemWords(loc, langKey).forEach((w) => {
        if (w.includes(mq) && !counts.has(w)) counts.set(w, menuTermCount(radiusFiltered, w, langKey));
      });
    });

    return [...counts.entries()]
      .filter(([w, n]) => n > 0 && !menuTerms.includes(w))
      .map(([w, n]) => ({ w, n }))
      .sort((a, b) => b.n - a.n || a.w.localeCompare(b.w))
      .slice(0, 12);
  }, [radiusFiltered, menuQuery, menuTerms, category, langKey]);

  const filtered = useMemo(
    () => radiusFiltered.filter((l) => passesOtherFacets(l, null)),
    [radiusFiltered, passesOtherFacets],
  );

  const sortOptions = useMemo(
    () =>
      category === "events"
        ? [["date", t.sortDate], ["distance", t.sortNearest], ["az", t.sortAz]]
        : [["distance", t.sortNearest], ["rating", t.sortRating], ["az", t.sortAz]],
    [category, t],
  );

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortKey === "distance") list.sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
    else if (sortKey === "rating") list.sort((a, b) => (b.overallRating || 0) - (a.overallRating || 0));
    else if (sortKey === "date") list.sort((a, b) => (a.eventDate ?? Infinity) - (b.eventDate ?? Infinity));
    else list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [filtered, sortKey]);

  // Ads only ever run on the real, finished pretty URLs (/no/.../...) —
  // never on /new-oversikt-preview (the query-param fallback route, still
  // semi-hidden/unfinished). Both render through this same component, so
  // this is the one place that actually decides whether an ad request can
  // happen at all, regardless of whether the page itself is crawlable.
  const sortedWithAds = useMemo(() => (isPrettyRoute ? interleaveAds(sorted) : sorted), [sorted, isPrettyRoute]);

  const locationsById = useMemo(() => new Map(sorted.map((loc) => [loc.id, loc])), [sorted]);

  // Same clustering as the app's map.js (Supercluster with the same radius/
  // maxZoom), rebuilt whenever the visible location set changes.
  const clusterIndex = useMemo(() => {
    const index = new Supercluster({ radius: 50, maxZoom: 14 });
    const points = sorted
      .filter((loc) => typeof loc.lat === "number" && typeof loc.lng === "number")
      .map((loc) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [loc.lng, loc.lat] },
        properties: { locationId: loc.id },
      }));
    index.load(points);
    return index;
  }, [sorted]);

  const mapClusters = useMemo(() => {
    if (!mapViewport) return [];
    return clusterIndex.getClusters(mapViewport.bounds, Math.round(mapViewport.zoom));
  }, [clusterIndex, mapViewport]);

  const updateMapViewport = useCallback((mapInstance) => {
    const b = mapInstance.getBounds();
    setMapViewport({
      zoom: mapInstance.getZoom(),
      bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
    });
  }, []);

  const handleClusterClick = useCallback(
    (cluster) => {
      const [lng, lat] = cluster.geometry.coordinates;
      const expansionZoom = Math.min(clusterIndex.getClusterExpansionZoom(cluster.id), 20);
      mapRef.current?.getMap().flyTo({ center: [lng, lat], zoom: expansionZoom, duration: 600 });
    },
    [clusterIndex],
  );

  const hasActiveFacets = selectedTypes.size > 0 || selectedPrices.size > 0 || menuTerms.length > 0 || radiusKm !== (city?.radiusKm || 25);
  const clearFacets = () => {
    setSelectedTypes(new Set());
    setSelectedPrices(new Set());
    setMenuTerms([]);
    setMenuQuery("");
    setRadiusKm(city?.radiusKm || 25);
  };

  const [detailCache, setDetailCache] = useState({}); // locationId -> full doc | undefined (loading)
  const listLocation = useMemo(
    () => allLocations.find((l) => l.id === selectedId) || allEvents.find((l) => l.id === selectedId) || null,
    [allLocations, allEvents, selectedId],
  );
  const selectedLocation = useMemo(() => {
    const full = detailCache[selectedId];
    // A direct place-URL visit has no list-cached version yet (the city
    // list may not even be loaded) — the full detail fetch alone already
    // has everything the profile needs, so don't wait on the list.
    if (!listLocation) return full || null;
    return full ? { ...listLocation, ...full } : listLocation;
  }, [listLocation, detailCache, selectedId]);
  const savedLocations = useMemo(
    () => [...allLocations, ...allEvents].filter((l) => savedIds.has(l.id)),
    [allLocations, allEvents, savedIds],
  );

  useEffect(() => {
    // Events have no separate "full detail" doc — fetchEventsNear already
    // returns everything there is, and events aren't in the `lokasjoner`
    // collection this looks up, so skip it rather than fetch a guaranteed
    // not-found every time an event profile opens.
    if (view !== "profile" || !selectedId || detailCache[selectedId] !== undefined || listLocation?.type === "event") return;
    fetchLocationDetail(selectedId)
      .then((full) => setDetailCache((prev) => ({ ...prev, [selectedId]: full })))
      .catch(() => setDetailCache((prev) => ({ ...prev, [selectedId]: null })));
  }, [view, selectedId, detailCache, listLocation]);

  // The venue hosting an event — eventOrganiser is a `lokasjoner` doc id
  // (confirmed against the app's own locationPageScreen.js, which reads it
  // the same way), fetched once per organiser and cached.
  const [organiserCache, setOrganiserCache] = useState({}); // organiserId -> location | undefined (loading) | null (not found)
  const organiserId = selectedLocation?.type === "event" ? selectedLocation.eventOrganiser : null;
  useEffect(() => {
    if (!organiserId || organiserCache[organiserId] !== undefined) return;
    fetchLocationDetail(organiserId)
      .then((loc) => setOrganiserCache((prev) => ({ ...prev, [organiserId]: loc })))
      .catch(() => setOrganiserCache((prev) => ({ ...prev, [organiserId]: null })));
  }, [organiserId, organiserCache]);

  // Tracks the place slug already resolved (by click or by URL), so the
  // deep-link effect below doesn't redundantly re-fetch by slug right after
  // a click already gave us the object directly.
  const handledPlaceSlugRef = useRef(null);

  // categoryOverride: for navigating to a location that isn't the currently
  // active tab's category (e.g. an event's organiser, which is a plain
  // restaurant/activity) — buildPlacePath needs THAT location's own
  // category to build a correct URL, not whatever tab happens to be open.
  const openProfile = (location, origin, categoryOverride) => {
    setSelectedId(location.id);
    setProfileOrigin(origin);
    setProfileTab("about");
    setLightboxIndex(null);
    setView("profile");
    if (isPrettyRoute && city?.countryCode) {
      const placePath = buildPlacePath({ lang, city, category: categoryOverride || category, location });
      if (placePath) {
        handledPlaceSlugRef.current = location.slug;
        navigate(placePath);
      }
    }
  };

  const goBackFromProfile = () => {
    if (isPrettyRoute && city?.countryCode) {
      const path = buildOversiktPath({ lang, city, category });
      if (path) navigate(path);
    }
    setView(profileOrigin);
  };

  // Deep link: /lang/country/city/category/place opened directly (shared
  // link, browser back/forward, page refresh). The slug alone is enough to
  // find the exact document — no need to wait for the whole city's location
  // list (a multi-geohash-range near() query) to load first.
  useEffect(() => {
    if (!isPrettyRoute || !routeParams.place) {
      handledPlaceSlugRef.current = null;
      return;
    }
    if (handledPlaceSlugRef.current === routeParams.place) return;

    // Mark as handled only once the fetch actually succeeds, not before it
    // starts — StrictMode's dev-only double-invoke (mount -> cleanup ->
    // mount) would otherwise have the *first* (cancelled) invocation claim
    // the ref before the *second* (real) invocation gets to run, so neither
    // one ever calls setView("profile").
    let cancelled = false;
    fetchLocationBySlug(routeParams.place)
      .then((full) => {
        if (cancelled || !full) return;
        handledPlaceSlugRef.current = routeParams.place;
        setSelectedId(full.id);
        setDetailCache((prev) => ({ ...prev, [full.id]: full })); // already the full doc — skip the extra detail fetch
        setProfileOrigin("list");
        setProfileTab("about");
        setView("profile");
      })
      .catch((err) => console.error("[OversiktPreview] fetchLocationBySlug failed:", err));
    return () => {
      cancelled = true;
    };
  }, [isPrettyRoute, routeParams.place]);

  useEffect(() => {
    if (view !== "profile" || profileTab !== "reviews" || !selectedId) return;
    if (reviews[selectedId] !== undefined) return;
    setReviews((prev) => ({ ...prev, [selectedId]: null })); // null = loading
    fetchReviewsFor(selectedId)
      .then((list) => setReviews((prev) => ({ ...prev, [selectedId]: list })))
      .catch(() => setReviews((prev) => ({ ...prev, [selectedId]: [] })));
  }, [view, profileTab, selectedId, reviews]);

  // Lightbox: Escape to close, arrow keys to step through the gallery.
  useEffect(() => {
    if (lightboxIndex === null) return;
    const galleryLength = selectedLocation?.gallery.length || 0;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      else if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % galleryLength);
      else if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + galleryLength) % galleryLength);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [lightboxIndex, selectedLocation]);

  const cityMatches = useMemo(() => {
    const q = citySearch.trim().toLowerCase();
    return q ? allCities.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 6) : [];
  }, [citySearch, allCities]);

  const goToCity = (c) => {
    setCitySearch("");
    setCitySearchOpen(false);
    const prettyPath = buildOversiktPath({ lang, city: c, category });
    navigate(prettyPath || `/new-oversikt-preview?city=${c.id}&category=${category}`);
  };

  // On the pretty route, the URL itself encodes the language, so switching
  // it navigates to the equivalent localized path (re-slugging country and
  // category) instead of just flipping local state, which the routeLang
  // sync effect above would otherwise immediately fight.
  const switchLang = (newLang) => {
    if (isPrettyRoute && city?.countryCode) {
      const prettyPath = buildOversiktPath({ lang: newLang, city, category });
      if (prettyPath) {
        navigate(prettyPath);
        return;
      }
    }
    setLang(newLang);
  };

  const mapGl = useMapGl();

  // The map container sits in a sticky/grid layout, so its true size can
  // still be settling (sticky positioning, list height changing as data
  // loads) at the moment mapbox-gl first measures it — that mismeasurement
  // is what left mapViewport stale (pins missing) until the user dragged
  // the map and forced a real recalculation. Watching the container
  // directly keeps our own bounds/zoom bookkeeping in sync without relying
  // on user interaction. ResizeObserver fires once immediately on
  // observe(), so this also covers the initial mount.
  //
  // Deliberately doesn't call map.resize() itself — mapbox-gl already runs
  // its own internal ResizeObserver on this same container (trackResize
  // defaults to true) and keeps the canvas sized correctly on its own. A
  // second, redundant resize() call racing that internal one during the
  // style's initial load is what was causing the map to render correctly
  // for a moment and then go blank/gray in production.
  useEffect(() => {
    if (!mapGl || !mapOpen) return;
    const el = mapContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      updateMapViewport(map);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mapGl, mapOpen, updateMapViewport]);

  if (cityLoading || !city) {
    return (
      <div className="mapliOversiktV2" style={{ padding: 40 }}>
        {cityLoading ? t.loading : "No city."}
      </div>
    );
  }

  const catLabelKey = { restaurants: "catRest", activities: "catAct", events: "catEv" };

  return (
    <div className="mapliOversiktV2" data-category={category} style={{ fontFamily: "var(--font-ui)" }}>
      <header style={{ position: "sticky", top: 0, zIndex: 40, background: "var(--whiteBackgroundColor)", borderBottom: "1px solid var(--border-hairline)" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "0 16px" : "0 32px", height: isMobile ? 58 : 72, display: "flex", alignItems: "center", gap: isMobile ? 12 : 20 }}>
          <button onClick={() => navigate("/new-landing-preview")} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", flex: "0 0 auto", display: "flex" }}>
            <img src={logoOrange} alt="Mapli" style={{ height: isMobile ? 20 : 24, display: "block" }} />
          </button>

          <div ref={citySearchRef} style={{ position: "relative", flex: isMobile ? "1 1 auto" : "0 1 260px", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--border-hairline)", borderRadius: "var(--radius-pill)", padding: "0 14px", height: 40 }}>
              <Icon name="search" size={18} color="var(--ink-500)" />
              <input
                type="text"
                className="searchInput"
                value={citySearch}
                onChange={(e) => {
                  setCitySearch(e.target.value);
                  setCitySearchOpen(true);
                }}
                onFocus={() => setCitySearchOpen(true)}
                placeholder={`${t.searchPlaceholder}: ${city.name}`}
                style={{ flex: 1, minWidth: 0, fontSize: 15 }}
              />
            </div>
            {citySearchOpen && cityMatches.length > 0 && (
              <div style={{ position: "absolute", left: 0, right: 0, top: 46, zIndex: 30, background: "var(--whiteBackgroundColor)", borderRadius: "var(--radius-panel)", boxShadow: "var(--shadow-float)", padding: 6 }}>
                {cityMatches.map((c) => (
                  <button key={c.id} onClick={() => goToCity(c)} style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "transparent", cursor: "pointer", padding: "8px 10px", borderRadius: "var(--radius-card)", font: "var(--type-body)" }}>
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: isMobile ? 10 : 18, flex: "0 0 auto" }}>
            <button
              onClick={() => setView(view === "saved" ? "list" : "saved")}
              className="navLink"
              style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, font: "var(--type-caption)", color: view === "saved" ? "var(--mapli-coral)" : "var(--ink-700)", whiteSpace: "nowrap" }}>
              <Icon name="favorite" size={18} />
              {!isMobile && t.savedNav}
              {savedIds.size > 0 && ` (${savedIds.size})`}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button onClick={() => switchLang("no")} className="navLink" style={{ border: "none", background: "transparent", cursor: "pointer", padding: "4px 2px", font: "var(--type-caption)", color: lang === "no" ? "var(--mapli-coral)" : "var(--ink-500)" }}>NO</button>
              <span style={{ color: "var(--ink-300)", font: "var(--type-caption)" }}>/</span>
              <button onClick={() => switchLang("en")} className="navLink" style={{ border: "none", background: "transparent", cursor: "pointer", padding: "4px 2px", font: "var(--type-caption)", color: lang === "en" ? "var(--mapli-coral)" : "var(--ink-500)" }}>EN</button>
            </div>
          </div>
        </div>

        {view !== "saved" && (
          <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "0 16px" : "0 32px" }}>
            <nav style={{ display: "flex", gap: 6, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              {CATEGORY_KEYS.map((key) => {
                const active = key === category;
                const colorVar = { restaurants: "var(--purpleRestaurantColor)", activities: "var(--greenActivityColor)", events: "var(--blueEventsColor)" }[key];
                return (
                  <button
                    key={key}
                    className="catTab"
                    onClick={() => {
                      setSelectedTypes(new Set());
                      if (isPrettyRoute && city?.countryCode) {
                        const prettyPath = buildOversiktPath({ lang, city, category: key });
                        if (prettyPath) {
                          navigate(prettyPath);
                          return;
                        }
                      }
                      setCategory(key);
                    }}
                    style={{
                      padding: isMobile ? (active ? "11px 14px 13px" : "9px 14px 11px") : (active ? "13px 20px 15px" : "11px 20px 13px"),
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "16px 16px 0 0",
                      background: active ? "var(--whiteBackgroundColor)" : colorVar,
                      color: active ? colorVar : "#fff",
                      font: isMobile ? "700 15px/1.2 var(--font-ui)" : "700 18px/1.2 var(--font-ui)",
                      boxShadow: active ? "0 -2px 6px rgba(23,22,20,0.08)" : "none",
                      flex: "0 0 auto",
                      whiteSpace: "nowrap",
                    }}>
                    {t[catLabelKey[key]]}
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {view === "list" && (
        <main style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "16px 16px 0" : "24px 32px 0" }}>
          <div style={{ font: "var(--type-caption)", color: "var(--ink-500)", display: "flex", gap: 8 }}>
            <button onClick={() => navigate("/new-landing-preview")} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "var(--ink-500)", font: "inherit" }}>
              {t.backToHome}
            </button>
            <span style={{ color: "var(--ink-300)" }}>›</span>
            <span style={{ color: "var(--ink-700)" }}>{city.name}</span>
          </div>

          <h1 style={{ margin: "16px 0 0", font: "var(--type-tab)", fontSize: isMobile ? 26 : 40, color: "var(--ink-900)" }}>
            {t[catLabelKey[category]]} i {city.name}
          </h1>

          <div style={{ marginTop: 22, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "260px minmax(0, 1fr)", gap: isMobile ? 18 : 32, alignItems: "start" }}>
            <aside style={{ display: "grid", gap: 22, position: isMobile ? "static" : "sticky", top: 140, maxHeight: isMobile ? "none" : "calc(100vh - 160px)", overflowY: isMobile ? "visible" : "auto", paddingRight: 4 }}>
              {isMobile && (
                <button
                  onClick={() => setFiltersOpen((v) => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    border: "1px solid var(--ink-150)",
                    borderRadius: "var(--radius-panel)",
                    background: "var(--whiteBackgroundColor)",
                    padding: "12px 16px",
                    cursor: "pointer",
                    font: "var(--type-body-strong)",
                    color: "var(--ink-900)",
                  }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name="tune" size={18} color={hasActiveFacets ? "var(--mapli-coral)" : "var(--ink-900)"} />
                    {t.filterToggle}
                  </span>
                  <Icon name={filtersOpen ? "expand_less" : "expand_more"} size={20} />
                </button>
              )}
              {(!isMobile || filtersOpen) && category === "restaurants" && (
                <section style={{ display: "grid", gap: 12, minHeight: 0 }}>
                  <div style={{ display: "grid", gap: 4 }}>
                    <h3 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 16, color: "var(--ink-900)" }}>{t.facetMenu}</h3>
                    <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{MENU_VOCAB.length} {t.vocabCountSuffix(city.name)}</span>
                  </div>

                  {menuTerms.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {menuTerms.map((w) => (
                        <button
                          key={w}
                          onClick={() => setMenuTerms((prev) => prev.filter((x) => x !== w))}
                          className="facetChip"
                          style={{ display: "flex", alignItems: "center", gap: 6, border: "none", cursor: "pointer", padding: "7px 12px", borderRadius: "var(--radius-pill)", background: "var(--category)", color: "#fff", font: "var(--type-body-strong)", fontSize: 15 }}>
                          {w} <Icon name="close" size={15} />
                        </button>
                      ))}
                    </div>
                  )}

                  <input
                    type="text"
                    className="searchInput"
                    value={menuQuery}
                    onChange={(e) => setMenuQuery(e.target.value)}
                    placeholder={t.menuSearchPlaceholder}
                    style={{ width: "100%", padding: "9px 14px", border: "1px solid var(--ink-150)", borderRadius: "var(--radius-pill)", background: "var(--whiteBackgroundColor)", fontSize: 15, color: "var(--ink-900)" }}
                  />

                  <div style={{ display: "grid", minHeight: 0, maxHeight: 208, overflowY: "auto", borderTop: "1px solid var(--border-hairline)", paddingTop: 4 }}>
                    {menuSuggestions.length === 0 && <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.noMenuMatches}</span>}
                    {menuSuggestions.map((sg) => (
                      <button
                        key={sg.w}
                        onClick={() => {
                          setMenuTerms((prev) => [...prev, sg.w]);
                          setMenuQuery("");
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", border: "none", background: "transparent", padding: "7px 2px", cursor: "pointer", font: "var(--type-body)", color: "var(--ink-900)" }}>
                        <span>{sg.w}</span>
                        <span style={{ marginLeft: "auto", font: "var(--type-caption)", color: "var(--ink-500)" }}>{sg.n}</span>
                      </button>
                    ))}
                  </div>

                  {menuTerms.length > 0 && <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.andHint}</span>}
                </section>
              )}

              {(!isMobile || filtersOpen) && typeFacetOptions.length > 0 && (
                <section style={{ display: "grid", gap: 10 }}>
                  <h3 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 16, color: "var(--ink-900)" }}>{t.facetType}</h3>
                  <div style={{ display: "grid", gap: 8 }}>
                    {typeFacetOptions.map(([value, count]) => {
                      const on = selectedTypes.has(value);
                      return (
                        <button
                          key={value}
                          onClick={() =>
                            setSelectedTypes((prev) => {
                              const next = new Set(prev);
                              on ? next.delete(value) : next.add(value);
                              return next;
                            })
                          }
                          style={{ display: "flex", alignItems: "center", gap: 10, border: "none", background: "transparent", cursor: "pointer", padding: 0, font: "var(--type-body)", color: "var(--ink-900)" }}>
                          <span style={{ width: 18, height: 18, borderRadius: 4, border: `1px solid ${on ? "var(--category)" : "var(--ink-300)"}`, background: on ? "var(--category)" : "transparent" }} />
                          <span>{value}</span>
                          <span style={{ marginLeft: "auto", font: "var(--type-caption)", color: "var(--ink-500)" }}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {(!isMobile || filtersOpen) && (
                <section style={{ display: "grid", gap: 10 }}>
                  <h3 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 16, color: "var(--ink-900)" }}>{t.facetPrice}</h3>
                  <div style={{ display: "grid", gap: 8 }}>
                    {priceFacetOptions.map(([key, tierNum, count]) => {
                      const on = selectedPrices.has(key);
                      const label = "$".repeat(tierNum);
                      return (
                        <button
                          key={key}
                          onClick={() =>
                            setSelectedPrices((prev) => {
                              const next = new Set(prev);
                              on ? next.delete(key) : next.add(key);
                              return next;
                            })
                          }
                          style={{ display: "flex", alignItems: "center", gap: 10, border: "none", background: "transparent", cursor: "pointer", padding: 0, font: "var(--type-body)", color: "var(--ink-900)" }}>
                          <span style={{ width: 18, height: 18, borderRadius: 4, border: `1px solid ${on ? "var(--category)" : "var(--ink-300)"}`, background: on ? "var(--category)" : "transparent" }} />
                          <span>{label}</span>
                          <span style={{ marginLeft: "auto", font: "var(--type-caption)", color: "var(--ink-500)" }}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {(!isMobile || filtersOpen) && (
                <section style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <h3 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 16, color: "var(--ink-900)" }}>{t.facetRadius}</h3>
                    <span style={{ font: "var(--type-meta)", color: "var(--ink-700)" }}>{radiusKm} km</span>
                  </div>
                  <input type="range" min={1} max={Math.max((city.radiusKm || 25) * 2, 50)} step={1} value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))} />
                </section>
              )}

              {(!isMobile || filtersOpen) && hasActiveFacets && (
                <button onClick={clearFacets} style={{ justifySelf: "start", border: "none", background: "transparent", padding: 0, cursor: "pointer", font: "var(--type-body-strong)", color: "var(--text-link)", textDecoration: "underline" }}>
                  {t.clearFilters}
                </button>
              )}
            </aside>

            <div style={{ minWidth: 0, display: "grid", gridTemplateColumns: isMobile || !mapOpen ? "minmax(0,1fr)" : "minmax(0,1fr) 340px", gap: isMobile ? 16 : 24, alignItems: "start" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap", paddingBottom: 14, borderBottom: "1px solid var(--border-hairline)" }}>
                  <span style={{ font: "var(--type-body)", color: "var(--ink-700)" }}>
                    {locationsLoading ? t.loading : t.resultCount(sorted.length)}
                  </span>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setMapOpen((v) => !v)} style={{ border: "1px solid var(--ink-150)", background: "var(--whiteBackgroundColor)", borderRadius: "var(--radius-pill)", padding: "8px 14px", cursor: "pointer", font: "var(--type-caption)", color: "var(--ink-900)" }}>
                      {mapOpen ? t.mapHide : t.mapShow}
                    </button>
                    <div ref={sortRef} style={{ position: "relative" }}>
                      <button onClick={() => setSortOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid var(--ink-150)", background: "var(--whiteBackgroundColor)", borderRadius: "var(--radius-pill)", padding: "8px 14px", cursor: "pointer", font: "var(--type-caption)", color: "var(--ink-900)" }}>
                        <Icon name="sort" size={16} />
                        {Object.fromEntries(sortOptions)[sortKey]}
                      </button>
                      {sortOpen && (
                        <div style={{ position: "absolute", right: 0, top: 40, zIndex: 30, minWidth: 180, background: "var(--whiteBackgroundColor)", borderRadius: "var(--radius-panel)", boxShadow: "var(--shadow-float)", padding: 6 }}>
                          {sortOptions.map(([key, label]) => (
                            <button
                              key={key}
                              className="sortOption"
                              onClick={() => {
                                setSortKey(key);
                                setSortOpen(false);
                              }}
                              style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "transparent", cursor: "pointer", padding: "8px 10px", borderRadius: "var(--radius-card)", font: "var(--type-body)", color: key === sortKey ? "var(--mapli-coral)" : "var(--ink-900)" }}>
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {sortedWithAds.map((loc) => {
                    if (loc.__ad) {
                      return (
                        <li key={loc.key}>
                          <AdSlot />
                        </li>
                      );
                    }
                    const status = openStatus(loc.hours);
                    const tags = displayTags(loc, langKey);
                    const about = displayAbout(loc, langKey);
                    return (
                      <li key={loc.id} className="resultRow" style={{ padding: "18px 6px", borderBottom: "1px solid var(--border-hairline)", borderRadius: "var(--radius-card)" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "64px minmax(0,1fr) 40px", gap: 16, alignItems: "start" }}>
                          <LogoWell src={loc.mainImage} alt={loc.name} />
                          <div style={{ minWidth: 0, display: "grid", gap: 8 }}>
                            <button onClick={() => openProfile(loc, "list")} style={{ justifySelf: "start", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left", font: "var(--type-card-title)", color: "var(--ink-900)" }}>
                              {loc.name}
                            </button>
                            {loc.type === "event" ? (
                              loc.eventOrganiserName && (
                                <span style={{ display: "flex", alignItems: "center", gap: 4, font: "var(--type-body-strong)", color: "var(--category)" }}>
                                  <Icon name="location_on" size={16} />
                                  {loc.eventOrganiserName}
                                </span>
                              )
                            ) : (
                              <RatingSummary location={loc} t={t} />
                            )}
                            {tags.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {tags.map((tag) => (
                                  <span key={tag} style={{ padding: "3px 12px", borderRadius: "var(--radius-pill)", background: "var(--category)", color: "#fff", font: "var(--type-tag)" }}>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            {menuTerms.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                                <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.onTheMenu}</span>
                                {matchedMenuTerms(loc, menuTerms, langKey).map((w) => (
                                  <span key={w} style={{ padding: "3px 13px", borderRadius: "var(--radius-pill)", background: "var(--category)", color: "#fff", font: "var(--type-tag)" }}>
                                    {w}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", font: "var(--type-meta)", color: "var(--ink-900)" }}>
                              {loc.distanceKm !== null && <span>{loc.distanceKm < 1 ? `${Math.round(loc.distanceKm * 1000)} m` : `${loc.distanceKm.toFixed(1)} km`}</span>}
                              {loc.type === "event" && formatEventWhen(loc, lang) && (
                                <span style={{ color: "var(--category)", fontWeight: 600 }}>{formatEventWhen(loc, lang)}</span>
                              )}
                              {status && (
                                <span style={{ color: status.open ? "var(--greenActivityColor)" : "var(--ink-500)" }}>
                                  {status.open ? t.closesAt(status.closesAt) : status.opensAt ? t.opensAt(status.opensAt) : t.closedToday}
                                </span>
                              )}
                            </div>
                            {about && (
                              <p style={{ margin: 0, font: "var(--type-body)", color: "var(--ink-900)", maxWidth: "46em" }}>
                                {about.slice(0, 80)}
                                {about.length > 80 && "…"}
                                <button onClick={() => openProfile(loc, "list")} style={{ border: "none", background: "transparent", padding: "0 0 0 6px", cursor: "pointer", font: "var(--type-body-strong)", color: "var(--text-link)" }}>
                                  {t.moreLabel}
                                </button>
                              </p>
                            )}
                          </div>
                          <button onClick={() => toggleSaved(loc.id)} className="heartBtn" aria-label="Lagre" style={{ border: "none", background: "transparent", cursor: "pointer", justifySelf: "end" }}>
                            <Icon name="favorite" size={22} color={savedIds.has(loc.id) ? "var(--mapli-coral)" : "var(--ink-300)"} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {!locationsLoading && sorted.length === 0 && <p style={{ margin: "28px 0 0", font: "var(--type-body)", color: "var(--ink-500)" }}>{t.noResults}</p>}
              </div>

              {mapOpen && (
                <div style={{ minWidth: 0, position: isMobile ? "static" : "sticky", top: 140 }}>
                  <div ref={mapContainerRef} style={{ position: "relative", borderRadius: "var(--radius-panel)", overflow: "hidden", height: isMobile ? 260 : 420, background: "var(--map-water, #a9dcf5)", boxShadow: "var(--shadow-float)" }}>
                    {mapGl ? (
                      <mapGl.Map
                        ref={mapRef}
                        initialViewState={{ longitude: city.lng, latitude: city.lat, zoom: radiusKm > 40 ? 9 : radiusKm > 15 ? 11 : 13 }}
                        mapboxAccessToken={MAPBOX_TOKEN}
                        mapStyle={MAP_STYLE_URL}
                        style={{ width: "100%", height: "100%" }}
                        onLoad={(evt) => {
                          // mapError lives on this component, not the map
                          // instance, so it would otherwise survive a
                          // close/reopen or a navigate-away-and-back (which
                          // unmounts and remounts a fresh mapboxgl.Map) and
                          // sit stale on top of an otherwise-fine map. A
                          // successful load means whatever went wrong before
                          // no longer applies.
                          setMapError(null);
                          updateMapViewport(evt.target);
                        }}
                        onMoveEnd={(evt) => updateMapViewport(evt.target)}
                        onError={(evt) => {
                          // Style/tile requests can fail silently (bad token
                          // scope, an unpublished Studio style, a blocked
                          // domain) leaving the canvas stuck on mapbox-gl's
                          // pre-style gray — surface the real reason instead
                          // of a mysteriously blank map.
                          console.error("[OversiktPreview] Mapbox GL error:", evt?.error || evt);
                          setMapError(evt?.error?.message || "Map failed to load");
                        }}>
                        {mapClusters.map((c) => {
                          const [lng, lat] = c.geometry.coordinates;
                          if (c.properties.cluster) {
                            const count = c.properties.point_count;
                            const size = count > 99 ? 40 : count > 9 ? 34 : 28;
                            return (
                              <mapGl.Marker key={`cluster-${c.id}`} longitude={lng} latitude={lat}>
                                <button
                                  onClick={() => handleClusterClick(c)}
                                  aria-label={`${count} steder`}
                                  style={{
                                    width: size,
                                    height: size,
                                    borderRadius: "50%",
                                    border: "2px solid #fff",
                                    background: "var(--category)",
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: 13,
                                    boxShadow: "0 1px 4px rgba(0,0,0,.35)",
                                    cursor: "pointer",
                                    display: "grid",
                                    placeItems: "center",
                                    padding: 0,
                                  }}>
                                  {count}
                                </button>
                              </mapGl.Marker>
                            );
                          }
                          const loc = locationsById.get(c.properties.locationId);
                          if (!loc) return null;
                          return (
                            <mapGl.Marker key={loc.id} longitude={lng} latitude={lat} anchor="bottom">
                              {/* Button's own box is exactly the icon (64x64) — anchor="bottom" pins
                                  THAT box to the real coordinate. The label is an absolutely
                                  positioned overlay below it so it never affects the anchor math
                                  or inflates the clickable area. */}
                              <button
                                onClick={() => openProfile(loc, "list")}
                                className="mapPin"
                                aria-label={loc.name}
                                style={{
                                  position: "relative",
                                  width: 28,
                                  height: 28,
                                  border: "none",
                                  background: "transparent",
                                  padding: 0,
                                  cursor: "pointer",
                                }}>
                                <img
                                  src={iconForLocation(loc)}
                                  alt=""
                                  style={{ width: 28, height: 28, filter: "drop-shadow(0 1px 2px rgba(0,0,0,.4))" }}
                                />
                                <span
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    marginTop: 2,
                                    maxWidth: 110,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#000",
                                    textShadow: "0 0 3px #fff, 0 0 3px #fff",
                                  }}>
                                  {loc.name}
                                </span>
                              </button>
                            </mapGl.Marker>
                          );
                        })}
                      </mapGl.Map>
                    ) : (
                      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", font: "var(--type-caption)", color: "var(--ink-700)" }}>{t.loading}</div>
                    )}
                    {mapError && (
                      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", padding: 20, textAlign: "center", background: "rgba(246,245,240,0.92)", font: "var(--type-caption)", color: "var(--ink-700)" }}>
                        {mapError}
                      </div>
                    )}
                  </div>
                  <span style={{ display: "block", marginTop: 8, font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.mapHint}</span>
                </div>
              )}
            </div>
          </div>
        </main>
      )}

      {view === "saved" && (
        <main style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "20px 16px 60px" : "32px 32px 80px" }}>
          <h1 style={{ margin: 0, font: "var(--type-tab)", fontSize: isMobile ? 26 : 36, color: "var(--ink-900)" }}>{t.savedTitle}</h1>
          {savedLocations.length === 0 ? (
            <p style={{ margin: "18px 0 0", font: "var(--type-body)", color: "var(--ink-500)" }}>{t.savedEmpty}</p>
          ) : (
            <ul style={{ margin: "24px 0 0", padding: 0, listStyle: "none" }}>
              {savedLocations.map((loc) => (
                <li key={loc.id} data-category={category} style={{ padding: "18px 6px", borderBottom: "1px solid var(--border-hairline)" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "64px minmax(0,1fr) 40px", gap: 16, alignItems: "start" }}>
                    <LogoWell src={loc.mainImage} alt={loc.name} />
                    <div style={{ minWidth: 0, display: "grid", gap: 6 }}>
                      <button onClick={() => openProfile(loc, "saved")} style={{ justifySelf: "start", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left", font: "var(--type-card-title)", color: "var(--ink-900)" }}>
                        {loc.name}
                      </button>
                    </div>
                    <button onClick={() => toggleSaved(loc.id)} className="heartBtn" aria-label="Fjern" style={{ border: "none", background: "transparent", cursor: "pointer" }}>
                      <Icon name="favorite" size={22} color="var(--mapli-coral)" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </main>
      )}

      {view === "profile" && selectedLocation && (
        <main style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "16px 16px 60px" : "24px 32px 80px" }}>
          <button onClick={goBackFromProfile} style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "none", background: "transparent", padding: 0, cursor: "pointer", font: "var(--type-body-strong)", color: "var(--text-link)" }}>
            <Icon name="arrow_back" size={20} />
            {t.backLabel}
          </button>

          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: isMobile ? "72px minmax(0,1fr)" : "104px minmax(0,1fr)", gap: isMobile ? 16 : 24, alignItems: "center" }}>
            <LogoWell src={selectedLocation.mainImage} alt={selectedLocation.name} size={isMobile ? 72 : 104} />
            <div style={{ minWidth: 0, display: "grid", gap: 10 }}>
              {displayTags(selectedLocation, langKey).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {displayTags(selectedLocation, langKey).map((tag) => (
                    <span key={tag} style={{ padding: "3px 13px", borderRadius: "var(--radius-pill)", background: "var(--category)", color: "#fff", font: "var(--type-tag)" }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <h1 style={{ margin: 0, font: "var(--type-tab)", fontSize: isMobile ? 22 : 36, color: "var(--ink-900)" }}>{selectedLocation.name}</h1>
              {selectedLocation.type === "event" ? (
                selectedLocation.eventOrganiserName && (
                  <span style={{ display: "flex", alignItems: "center", gap: 4, font: "var(--type-body-strong)", fontSize: 17, color: "var(--category)" }}>
                    <Icon name="location_on" size={18} />
                    {selectedLocation.eventOrganiserName}
                  </span>
                )
              ) : (
                <RatingSummary location={selectedLocation} t={t} />
              )}
            </div>
          </div>

          {selectedLocation.type === "event" && (
            <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface-app)", borderRadius: "var(--radius-pill)", padding: "10px 18px" }}>
                <Icon name="calendar_today" size={18} color="var(--category)" />
                <span style={{ font: "var(--type-body-strong)" }}>{formatEventWhen(selectedLocation, lang)?.split(" · ")[0] || "---"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface-app)", borderRadius: "var(--radius-pill)", padding: "10px 18px" }}>
                <Icon name="schedule" size={18} color="var(--category)" />
                <span style={{ font: "var(--type-body-strong)" }}>{selectedLocation.eventTime || "---"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface-app)", borderRadius: "var(--radius-pill)", padding: "10px 18px" }}>
                <Icon name="payments" size={18} color="var(--category)" />
                <span style={{ font: "var(--type-body-strong)" }}>
                  {selectedLocation.averagePrice ? `${selectedLocation.currency || "kr"} ${selectedLocation.averagePrice}` : "---"}
                </span>
              </div>
            </div>
          )}

          <nav style={{ marginTop: 26, display: "flex", gap: isMobile ? 16 : 22, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            {(() => {
              const isEventProfile = selectedLocation.type === "event";
              const organiser = organiserId ? organiserCache[organiserId] : undefined;
              return [
                ["about", t.tabAbout],
                // No menu/activity-list data model for events, and "type"
                // here is the location actually being viewed (could be an
                // event's organiser, a plain restaurant/activity) — not
                // whatever the outer category tab happens to be.
                ...(isEventProfile ? [] : [["menu", selectedLocation.type === "activity" ? t.tabActivities : t.tabMenu]]),
                // Events get a link straight to the hosting venue instead of
                // reviews (events aren't reviewed themselves).
                ...(isEventProfile ? (organiserId ? [["organiser", t.tabOrganiser]] : []) : [["reviews", t.tabReviews]]),
              ].map(([key, label]) =>
                key === "organiser" ? (
                  <button
                    key={key}
                    disabled={!organiser}
                    onClick={() => organiser && openProfile(organiser, profileOrigin, categoryForType(organiser.type))}
                    className="profileTab"
                    style={{ border: "none", background: "transparent", cursor: organiser ? "pointer" : "default", padding: "0 0 10px", font: "var(--type-body-strong)", fontSize: 17, color: organiser ? "var(--text-link)" : "var(--ink-300)", display: "flex", alignItems: "center", gap: 4, flex: "0 0 auto", whiteSpace: "nowrap" }}>
                    <Icon name="open_in_new" size={16} />
                    {label}
                  </button>
                ) : (
                  <button
                    key={key}
                    onClick={() => setProfileTab(key)}
                    className={`profileTab ${profileTab === key ? "active" : ""}`}
                    style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0 0 10px", font: "var(--type-body-strong)", fontSize: 17, color: profileTab === key ? "var(--category)" : "var(--ink-700)", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                    {label}
                  </button>
                ),
              );
            })()}
          </nav>
          <div style={{ height: 1, background: "var(--border-hairline)" }} />

          {profileTab === "about" && (
            <div style={{ marginTop: 26, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 320px", gap: isMobile ? 24 : 32 }}>
              <div style={{ minWidth: 0, display: "grid", gap: 24 }}>
                {displayAbout(selectedLocation, langKey) && (
                  <section style={{ display: "grid", gap: 10 }}>
                    <p style={{ margin: 0, font: "var(--type-body)", lineHeight: 1.6, color: "var(--ink-900)" }}>{displayAbout(selectedLocation, langKey)}</p>
                  </section>
                )}
                {selectedLocation.type === "event" && (pickLang(selectedLocation.aboutEventLang, langKey) || selectedLocation.aboutEvent) && (
                  <section style={{ display: "grid", gap: 10 }}>
                    <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)" }}>{t.eventInfo}</h2>
                    <p style={{ margin: 0, font: "var(--type-body)", lineHeight: 1.6, color: "var(--ink-900)", whiteSpace: "pre-line" }}>
                      {linkifyText(pickLang(selectedLocation.aboutEventLang, langKey) || selectedLocation.aboutEvent)}
                    </p>
                  </section>
                )}
                {selectedLocation.gallery.length > 0 && (
                  <section style={{ display: "grid", gap: 10 }}>
                    <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)" }}>{t.gallery}</h2>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                      {selectedLocation.gallery.map((src, i) => (
                        <button
                          key={src}
                          onClick={() => setLightboxIndex(i)}
                          aria-label={t.viewLarger}
                          style={{ border: "none", background: "transparent", padding: 0, cursor: "zoom-in", display: "block" }}>
                          <img src={src} alt="" style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", borderRadius: "var(--radius-card)" }} />
                        </button>
                      ))}
                    </div>
                  </section>
                )}
              </div>
              <div style={{ minWidth: 0, display: "grid", gap: 20, alignContent: "start" }}>
                {selectedLocation.hours && (
                  <section style={{ display: "grid", gap: 10 }}>
                    <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)" }}>{t.hours}</h2>
                    <div style={{ background: "var(--surface-app)", borderRadius: "var(--radius-panel)", padding: 16, display: "grid", gap: 6 }}>
                      {formatHoursWeek(selectedLocation.hours, { days: t.days, closed: t.closedToday }).map((h) => (
                        <div key={h.day} style={{ display: "flex", justifyContent: "space-between", font: "var(--type-body)" }}>
                          <span>{h.day}</span>
                          <span style={{ font: "var(--type-body-strong)" }}>{h.value}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                {(selectedLocation.phone || selectedLocation.website || selectedLocation.bookingLink) && (
                  <section style={{ display: "grid", gap: 10 }}>
                    <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)" }}>{t.reserve}</h2>
                    <div style={{ background: "var(--surface-app)", borderRadius: "var(--radius-panel)", padding: 16, display: "grid", gap: 10 }}>
                      {selectedLocation.phone && (
                        <div style={{ display: "grid", gap: 2 }}>
                          <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.phone}</span>
                          <span style={{ font: "var(--type-body-strong)", color: "var(--category)" }}>{selectedLocation.phone}</span>
                        </div>
                      )}
                      {selectedLocation.website && (
                        <div style={{ display: "grid", gap: 2, minWidth: 0 }}>
                          <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.website}</span>
                          <a href={selectedLocation.website} target="_blank" rel="noreferrer" style={{ font: "var(--type-body-strong)", color: "var(--category)", overflowWrap: "anywhere" }}>
                            {selectedLocation.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </section>
                )}
                {typeof selectedLocation.lat === "number" && selectedLocation.type !== "event" && (
                  <section style={{ display: "grid", gap: 10 }}>
                    <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)" }}>{t.findUs}</h2>
                    <img
                      alt="map"
                      style={{ width: "100%", borderRadius: "var(--radius-panel)", boxShadow: "var(--shadow-float)" }}
                      src={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-s+f9615b(${selectedLocation.lng},${selectedLocation.lat})/${selectedLocation.lng},${selectedLocation.lat},14,0,0/320x220?access_token=${MAPBOX_TOKEN}`}
                    />
                  </section>
                )}
              </div>
            </div>
          )}

          {profileTab === "menu" && (
            <div style={{ marginTop: 26, maxWidth: 760, display: "grid", gap: 16 }}>
              {selectedLocation.allergyNote && (
                <div style={{ background: "var(--surface-app)", borderRadius: "var(--radius-panel)", padding: "18px 20px", display: "grid", gap: 8 }}>
                  <span style={{ font: "var(--type-body-strong)", fontSize: 17, color: "var(--category)" }}>{t.allergyInfo}</span>
                  <span style={{ font: "var(--type-body)", color: "var(--ink-900)" }}>{selectedLocation.allergyNote}</span>
                </div>
              )}
              {(() => {
                const cats = selectedLocation.menus || pickLang(selectedLocation.menuListLang, langKey);
                if (!cats || cats.length === 0) return <p style={{ margin: 0, font: "var(--type-body)", color: "var(--ink-500)" }}>{selectedLocation.type === "activity" ? t.noActivities : t.noMenu}</p>;
                return <MenuAccordion cats={cats} currency={selectedLocation.currency} key={selectedLocation.id} />;
              })()}
              {selectedLocation.menusChanged && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <span style={{ font: "var(--type-caption)", color: "var(--ink-500)" }}>
                    {t.lastUpdated(new Date(selectedLocation.menusChanged).toLocaleDateString(lang === "no" ? "nb-NO" : "en-US"))}
                  </span>
                </div>
              )}
            </div>
          )}

          {profileTab === "reviews" && (
            <div style={{ marginTop: 26, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "300px minmax(0,1fr)", gap: isMobile ? 24 : 32 }}>
              <div style={{ display: "grid", gap: 22, alignContent: "start" }}>
                <section style={{ display: "grid", gap: 12 }}>
                  <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)" }}>{t.overallRating}</h2>
                  <div style={{ background: "var(--surface-app)", borderRadius: "var(--radius-panel)", padding: 20, display: "grid", gap: 10 }}>
                    <RatingStars rating={selectedLocation.overallRating || 0} size={22} />
                    <span style={{ font: "var(--type-body-strong)", fontSize: 20, color: "var(--ink-900)" }}>
                      {selectedLocation.overallRating ? `${selectedLocation.overallRating.toFixed(1)}/5` : t.noReviews}
                    </span>
                    <span style={{ font: "var(--type-body)", fontStyle: "italic", color: "var(--category)" }}>{t.overallNote}</span>
                  </div>
                </section>
                {Object.entries(selectedLocation.ratings)
                  .filter(([, r]) => r.count > 0)
                  .map(([name, r]) => (
                    <section key={name} style={{ display: "grid", gap: 12 }}>
                      <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)", textTransform: "capitalize" }}>{name}</h2>
                      <div style={{ background: "var(--surface-app)", borderRadius: "var(--radius-panel)", padding: 20, display: "grid", gap: 8 }}>
                        <span style={{ font: "var(--type-body-strong)", fontSize: 19, color: "var(--ink-900)" }}>{r.score.toFixed(1)}/5</span>
                        <span style={{ font: "var(--type-body)", fontStyle: "italic", color: "var(--category)" }}>
                          {r.count} {name} {t.reviewCountSuffix}
                        </span>
                      </div>
                    </section>
                  ))}
              </div>
              <div style={{ display: "grid", gap: 28, alignContent: "start" }}>
                {reviews[selectedLocation.id] === null && <p style={{ margin: 0, font: "var(--type-body)", color: "var(--ink-500)" }}>{t.loading}</p>}
                {Array.isArray(reviews[selectedLocation.id]) && reviews[selectedLocation.id].length === 0 && (
                  <p style={{ margin: 0, font: "var(--type-body)", fontStyle: "italic", color: "var(--ink-500)" }}>{t.noReviews}</p>
                )}
                {Array.isArray(reviews[selectedLocation.id]) &&
                  REVIEW_SOURCE_ORDER.filter((source) => reviews[selectedLocation.id].some((rv) => rv.source === source)).map((source) => {
                    const sourceReviews = reviews[selectedLocation.id].filter((rv) => rv.source === source).slice(0, 5);
                    return (
                      <section key={source} style={{ display: "grid", gap: 14 }}>
                        <h2 style={{ margin: 0, font: "var(--type-body-strong)", fontSize: 18, color: "var(--ink-900)" }}>
                          {t.reviewsHeadingFor(REVIEW_SOURCE_LABEL[source] || source)}
                        </h2>
                        {sourceReviews.map((rv, i) => (
                          <article key={i} style={{ background: "var(--surface-app)", borderRadius: "var(--radius-panel)", padding: 20, display: "grid", gridTemplateColumns: "64px minmax(0,1fr)", gap: 18, alignItems: "start" }}>
                            <div style={{ display: "grid", gap: 8, justifyItems: "center" }}>
                              <LogoWell src={rv.avatarUrl} alt={rv.author || rv.source} size={64} />
                              <span style={{ font: "var(--type-caption)", color: "var(--ink-700)", textAlign: "center", overflowWrap: "anywhere" }}>
                                {rv.author || rv.source}
                              </span>
                            </div>
                            <div style={{ minWidth: 0, display: "grid", gap: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                {rv.rating !== null && <RatingStars rating={rv.rating} size={16} />}
                                <span style={{ font: "var(--type-body)", color: "var(--ink-700)" }}>{rv.date}</span>
                              </div>
                              {rv.title && <span style={{ font: "var(--type-body-strong)", fontSize: 19, color: "var(--ink-900)" }}>{rv.title}</span>}
                              <p style={{ margin: 0, font: "var(--type-body)", lineHeight: 1.55, color: "var(--ink-900)" }}>{rv.text}</p>
                              {rv.visitedRaw && <span style={{ font: "var(--type-body-strong)", color: "var(--ink-700)" }}>{t.visitedLabel(rv.visitedRaw)}</span>}
                            </div>
                          </article>
                        ))}
                      </section>
                    );
                  })}
              </div>
            </div>
          )}
        </main>
      )}

      <section style={{ background: "var(--mapli-coral)", color: "#fff", marginTop: 40 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "24px 16px" : "34px 32px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 18 }}>
          <img src={symbolOnDark} alt="" style={{ height: isMobile ? 40 : 48 }} />
          <div style={{ minWidth: 220, flex: 1, display: "grid", gap: 4 }}>
            <span style={{ font: "var(--type-card-title)", fontSize: 22, color: "#fff" }}>{t.appTitle}</span>
            <span style={{ font: "var(--type-body)", color: "#fff" }}>{t.appBody}</span>
          </div>
        </div>
      </section>
      <footer style={{ borderTop: "1px solid var(--border-hairline)" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "16px 16px" : "20px 32px", font: "var(--type-caption)", color: "var(--ink-500)" }}>{t.footer}</div>
      </footer>

      <AdConsentBanner consent={adConsent} onChoose={chooseAdConsent} lang={lang} />

      {lightboxIndex !== null && selectedLocation?.gallery[lightboxIndex] && (
        <Lightbox
          src={selectedLocation.gallery[lightboxIndex]}
          label={selectedLocation.name}
          onClose={() => setLightboxIndex(null)}
          onPrev={selectedLocation.gallery.length > 1 ? () => setLightboxIndex((i) => (i - 1 + selectedLocation.gallery.length) % selectedLocation.gallery.length) : null}
          onNext={selectedLocation.gallery.length > 1 ? () => setLightboxIndex((i) => (i + 1) % selectedLocation.gallery.length) : null}
        />
      )}
    </div>
  );
};

export default OversiktPreview;
